import { useEffect, useState, createContext, useContext, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, PanelLeft, Home, Calculator, FileText, Map, Users, Building2, Settings, Download, BarChart3, FileBarChart } from 'lucide-react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import AppSidebar from './AppSidebar';
import LongTermListingsPopup from './LongTermListingsPopup';

interface AppLayoutProps {
  children: React.ReactNode;
}

// Context for header actions
const HeaderActionsContext = createContext<{
  setHeaderActions: (actions: React.ReactNode) => void;
}>({
  setHeaderActions: () => {},
});

export const useHeaderActions = () => {
  return useContext(HeaderActionsContext);
};

export default function AppLayout({ children }: AppLayoutProps) {
  const [email, setEmail] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isBrandUser, setIsBrandUser] = useState(false);
  const [navigationPermissions, setNavigationPermissions] = useState<{ browse?: boolean; campaigns?: boolean; longTermListings?: boolean; reservations?: boolean; export?: boolean } | null>(null);
  const [showLongTermPopup, setShowLongTermPopup] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarMinimized, setSidebarMinimized] = useState(false);
  const [headerActions, setHeaderActions] = useState<React.ReactNode>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Auto-minimize sidebar on small screens (below md breakpoint)
  useEffect(() => {
    const checkScreenSize = () => {
      // Only auto-minimize if screen is below md breakpoint (768px)
      // This ensures sidebar is minimized when viewport is small
      if (window.innerWidth < 768) {
        setSidebarMinimized(true);
      }
    };

    // Check on mount
    checkScreenSize();
    
    // Listen for resize events
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/auth/me', { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        setEmail(data.email || null);
        const roles: string[] = data.roles || [];
        setIsAdmin(roles.includes('admin') || roles.includes('superadmin'));
        setIsBrandUser(!!data.brandId);
        setNavigationPermissions(data.navigationPermissions || null);
        
        // Only show popup if flag is set, user is not admin, and hasn't dismissed it this session
        const hasDismissed = sessionStorage.getItem('longTermPopupDismissed') === 'true';
        if (data.showLongTermPopup && !hasDismissed && !roles.includes('admin') && !roles.includes('superadmin')) {
          setShowLongTermPopup(true);
        } else {
          setShowLongTermPopup(false);
        }
      } catch {}
    })();
  }, [location.pathname]);

  // Clear header actions when route changes
  useEffect(() => {
    setHeaderActions(null);
  }, [location.pathname]);

  async function onLogout() {
    try {
      await fetch('/auth/logout', { method: 'POST' });
    } finally {
      navigate('/login');
    }
  }

  const handleMobileNav = () => {
    setMobileMenuOpen(false);
  };

  // Get page info based on current route
  const getPageInfo = () => {
    const path = location.pathname;
    
    if (path === '/') {
      return { icon: Home, label: 'Browse' };
    } else if (path === '/calculator') {
      return { icon: Calculator, label: 'Campaigns' };
    } else if (path === '/new-location-tender') {
      return { icon: FileText, label: 'Long\u2011Term Listings' };
    } else if (path === '/reservations') {
      return { icon: FileText, label: 'My Reservations' };
    } else if (path === '/export') {
      return { icon: Download, label: 'Machine List Export' };
    } else if (path.startsWith('/admin/map')) {
      return { icon: Map, label: 'Machine Editor' };
    } else if (path.startsWith('/admin/users')) {
      return { icon: Users, label: 'Users' };
    } else if (path.startsWith('/admin/brands')) {
      return { icon: Building2, label: 'Brands' };
    } else if (path.startsWith('/admin/reports')) {
      return { icon: BarChart3, label: 'Reports' };
    } else if (path.startsWith('/admin/custom-reports')) {
      return { icon: FileBarChart, label: 'Custom Reports' };
    } else if (path.startsWith('/admin/reservations')) {
      return { icon: FileText, label: 'Pending Requests' };
    } else if (path.startsWith('/admin/field-options')) {
      return { icon: Settings, label: 'Settings' };
    }
    
    // Default fallback
    return { icon: Home, label: 'Browse' };
  };

  const pageInfo = getPageInfo();
  const PageIcon = pageInfo.icon;

  return (
    <div className="flex h-screen bg-blue-600">
      {/* Desktop Sidebar */}
      <div className={`hidden md:flex fixed left-0 top-0 h-screen z-50 transition-all duration-300 ${sidebarMinimized ? 'w-16' : 'w-60'}`}>
        <aside className="w-full h-full relative">
          <AppSidebar
            email={email}
            isAdmin={isAdmin}
            isBrandUser={isBrandUser}
            navigationPermissions={navigationPermissions}
            onLogout={onLogout}
            minimized={sidebarMinimized}
          />
        </aside>
      </div>

      {/* Mobile Hamburger Menu - Only visible on mobile screens */}
      <div className="md:hidden fixed top-3 left-3 z-50">
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" size="icon" className="h-10 w-10">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-60 p-0">
            <AppSidebar
              email={email}
              isAdmin={isAdmin}
              isBrandUser={isBrandUser}
              navigationPermissions={navigationPermissions}
              onLogout={onLogout}
              onNavigate={handleMobileNav}
            />
          </SheetContent>
        </Sheet>
      </div>

      {/* Main Content */}
      <main className={`flex-1 overflow-auto pt-14 md:pt-0 transition-all duration-300 relative z-10 ${sidebarMinimized ? 'md:ml-16' : 'md:ml-60'}`}>
        <div className="h-full pt-1 px-1 md:pt-2 md:px-2">
          <div className="h-full bg-white rounded-t-xl shadow-sm flex flex-col overflow-hidden relative z-10">
            {/* Header Bar - Shows on all pages */}
            <div className="flex items-center justify-between gap-2 md:gap-3 px-3 md:px-6 py-3 md:py-4 border-b border-gray-200 rounded-t-xl">
              <div className="flex items-center gap-2 md:gap-3">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSidebarMinimized(!sidebarMinimized)}
                  className="hidden md:flex h-8 w-8 bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 rounded-lg"
                  title={sidebarMinimized ? "Expand sidebar" : "Minimize sidebar"}
                >
                  <PanelLeft className="h-4 w-4" />
                </Button>
                <div className="hidden md:block w-1 h-6 bg-gray-300 rounded-full"></div>
                <PageIcon className="w-4 h-4 md:w-5 md:h-5 text-gray-700 flex-shrink-0" />
                <span className="font-semibold text-sm md:text-base text-gray-900 truncate">{pageInfo.label}</span>
              </div>
              {headerActions && (
                <div className="flex items-center gap-3 ml-auto">
                  {headerActions}
                </div>
              )}
            </div>
            <div className="flex-1 overflow-auto p-2 md:p-4">
              <HeaderActionsContext.Provider value={useMemo(() => ({ setHeaderActions }), [setHeaderActions])}>
                {children}
              </HeaderActionsContext.Provider>
            </div>
          </div>
        </div>
      </main>
      
      {/* Long-Term Listings Popup */}
      {showLongTermPopup && !isAdmin && (
        <LongTermListingsPopup
          open={showLongTermPopup}
          onClose={() => setShowLongTermPopup(false)}
        />
      )}
    </div>
  );
}

